import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, MenuItem, Select, Skeleton, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { AccordionCard, CountrySelect, FieldWrapper, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetReferenceTypesQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { useLazyGetClientsSuppliersByRfcQuery } from "@api/proveedorClienteApi";
import { useLazyGetReferencesQuery, usePatchReferencesMutation, usePostReferencesMutation } from "@api/referenciasApi";
import { ApiError, ClientSupplier, ReferenceType } from "@interfaces/response";
import { ReferenceStructure } from "@interfaces/request";
import {
  Referencia,
  FormValues,
  referenciaVacia,
  firmaTelefono,
  estaCompleta,
  aReferencias,
  opcionesDisponibles,
} from "./referencias.helpers";
import { ALTO_SKELETON_EXPEDIENTE, PATHS_EXPEDIENTE_V2, REFERENCES_CODES } from "@helpers/constantes";
import { getFormikArrayError, getReferenceConfig, getReferenceIndex } from "@helpers/funciones";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { UUID } from "crypto";
import useReferenciasForm from "@hooks/useReferenciasForm";

const REFERENCIAS_POR_TIPO = 3;

export const ExpReferenciasClientes = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const client = useSelector((state: RootState) => state.register.personal_data);
  const company = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);

  const [getReferenceTypes] = useLazyGetReferenceTypesQuery();
  const [getClientsSuppliersByRFC] = useLazyGetClientsSuppliersByRfcQuery();
  const [getReferences] = useLazyGetReferencesQuery();
  const [triggerPostReferences] = usePostReferencesMutation();
  const [triggerPatchReferences] = usePatchReferencesMutation();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [openSet, setOpenSet] = useState<Set<number>>(new Set([0, 1, 2]));
  const [config, setConfig] = useState({ min: REFERENCIAS_POR_TIPO, required: REFERENCIAS_POR_TIPO });
  const [tipo, setTipo] = useState<ReferenceType | null>(null);
  const [catalogo, setCatalogo] = useState<ClientSupplier[]>([]);
  const initialRefsJson = useRef<string>("");

  const form = useReferenciasForm("clientes");

  const irAProveedores = () => navigate(PATHS_EXPEDIENTE_V2.referenciasProveedores.path);
  const volverAlExpediente = () => navigate(PATHS_EXPEDIENTE_V2.empresa.path);

  const buildSchema = (minRef: number, frm: ReturnType<typeof useReferenciasForm>) =>
    yup.object().shape({
      referencias: yup.array().of(
        yup.object().shape({
          rfc: yup.string().test("req-rfc", "Campo requerido", function (value) {
            return getReferenceIndex(this.path) < minRef ? Boolean(value) : true;
          }),
          country_code: yup.string().test("req-cc", "Campo requerido", function (value) {
            if (!frm.isVisible("phone_number")) return true;
            return getReferenceIndex(this.path) < minRef ? Boolean(value) : true;
          }),
          phone: yup
            .string()
            .test("req-phone", "Campo requerido", function (value) {
              if (!frm.isVisible("phone_number")) return true;
              return getReferenceIndex(this.path) < minRef ? Boolean(value) : true;
            })
            .matches(PHONE_NUMBER, { message: "Número de teléfono inválido", excludeEmptyString: true })
            .test("unique-phone", "El teléfono ya se encuentra registrado.", function () {
              const parent = this.parent as Referencia;
              if (!parent.phone) return true;
              const lista: Referencia[] = (this.from?.[1]?.value as FormValues | undefined)?.referencias ?? [];
              const propia = firmaTelefono(parent);
              return lista.filter((item) => firmaTelefono(item) === propia).length <= 1;
            }),
          contact_name: yup
            .string()
            .test("req-name", "Campo requerido", function (value) {
              if (!frm.isRequired("contact_name", true)) return true;
              return getReferenceIndex(this.path) < minRef ? Boolean(value) : true;
            })
            .test("unique-name", "El nombre de contacto ya se encuentra registrado.", function (value) {
              if (!value || !frm.isVisible("contact_name")) return true;
              const lista: Referencia[] = (this.from?.[1]?.value as FormValues | undefined)?.referencias ?? [];
              const normalizar = (n: string) => (n || "").replaceAll(" ", "").toLowerCase();
              return lista.filter((item) => normalizar(item.contact_name) === normalizar(value)).length <= 1;
            }),
          email: yup
            .string()
            .test("req-email", "Campo requerido", function (value) {
              if (!frm.isRequired("email", true)) return true;
              return getReferenceIndex(this.path) < minRef ? Boolean(value) : true;
            })
            .matches(EMAIL, {
              message: "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)",
              excludeEmptyString: true,
            })
            .test("unique-email", "El email ya se encuentra registrado.", function (value) {
              if (!value || !frm.isVisible("email")) return true;
              const lista: Referencia[] = (this.from?.[1]?.value as FormValues | undefined)?.referencias ?? [];
              return lista.filter((item) => item.email === value).length <= 1;
            }),
        }),
      ),
    });

  const validationSchema = buildSchema(config.min, form);

  const formik = useFormik<FormValues>({
    initialValues: { referencias: Array.from({ length: REFERENCIAS_POR_TIPO }, referenciaVacia) },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        if (!tipo) return;

        for (const referencia of values.referencias) {
          if (!estaCompleta(referencia, form.isVisible)) continue;

          const elegido = catalogo.find((item) => item.rfc === referencia.rfc);
          const body = {
            client_details_id: client.id,
            id_ext: "3333",
            reference_type_id: tipo.id,
            company_name: elegido?.name,
            company_rfc: elegido?.rfc,
            contact_name: referencia.contact_name,
            email: referencia.email,
            code_phone_number: referencia.country_code,
            phone_number: referencia.phone,
            phone_extension: referencia.phone_extension,
            percentage_share: Number(elegido?.share),
          };

          if (referencia.id) {
            await triggerPatchReferences({ referenceId: referencia.id as UUID, body }).unwrap();
          } else {
            await triggerPostReferences(body).unwrap();
          }
        }

        irAProveedores();
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpReferenciasClientes-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue, validateForm } = formik;

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!client.id) return;

      // if (application.references_status === "COMPLETO" || application.references_status === "VALIDADO") {
      //   volverAlExpediente();
      // }

      const [tiposReferencia, catalogoSat, taxSystems] = await Promise.all([
        getReferenceTypes().unwrap(),
        getClientsSuppliersByRFC(String(company?.rfc)).unwrap(),
        getTaxSystems(application.type).unwrap(),
      ]);

      const { total_items: required, min_items: min } = getReferenceConfig(
        taxSystems?.config_extra?.references_config?.[REFERENCES_CODES.cliente],
      );
      setConfig({ min, required });

      const tipoClientes = tiposReferencia.find((item) => item.code === REFERENCES_CODES.cliente) ?? null;
      setTipo(tipoClientes);
      setCatalogo(catalogoSat.clients);

      const guardadas = tipoClientes
        ? await getReferences({ clientDetailsId: client.id, referenceTypeId: tipoClientes.id }).unwrap()
        : ([] as ReferenceStructure[]);

      const initialReferencias = aReferencias(guardadas, required, "company_rfc");
      initialRefsJson.current = JSON.stringify({ referencias: initialReferencias });
      setFieldValue("referencias", initialReferencias);
      setTimeout(() => validateForm(), 200);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpReferenciasClientes-initial_fetch" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client.id,
    company?.rfc,
    application.type,
    getReferenceTypes,
    getClientsSuppliersByRFC,
    getTaxSystems,
    getReferences,
    setFieldValue,
    validateForm,
    dispatch,
  ]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const lista = formik.values.referencias;
  const configLoading = form.isConfigLoading;
  const completas = lista.filter((ref) => estaCompleta(ref, form.isVisible)).length;
  const todasCompletas = completas === config.required;
  const bloqueado = isLoading || configLoading || formik.isSubmitting;

  const hasUnsavedChanges =
    !isLoading && initialRefsJson.current !== "" && JSON.stringify(formik.values) !== initialRefsJson.current;

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente · Empresa · Referencias comerciales · Clientes"
        title="Referencias de clientes"
        subtitle="Agrega clientes de la empresa que podamos contactar como referencia comercial."
      />

      {isLoading || configLoading ? (
        <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON_EXPEDIENTE, borderRadius: 2 }} />
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {lista.map((referencia, index) => (
              <AccordionCard
                key={`cliente_${index}`}
                index={index + 1}
                title={`Cliente ${index + 1}`}
                open={openSet.has(index)}
                onToggle={() =>
                  setOpenSet((prev) => {
                    const next = new Set(prev);
                    next.has(index) ? next.delete(index) : next.add(index);
                    return next;
                  })
                }
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FieldWrapper label="Cliente" full error={getFormikArrayError(formik, "referencias", index, "rfc")}>
                    <Select
                      size="lendia_input"
                      name={`referencias[${index}].rfc`}
                      fullWidth
                      displayEmpty
                      value={referencia.rfc}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={bloqueado}
                      inputProps={{ "data-testid": `expediente-referencias-clientes-${index}-select` }}
                    >
                      <MenuItem value="">Nombre o razón social del cliente</MenuItem>
                      {opcionesDisponibles(index, catalogo, lista).map((item) => (
                        <MenuItem key={item.rfc} value={item.rfc}>
                          {item.name.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>

                  {form.isVisible("contact_name") && (
                    <FieldWrapper
                      label="Nombre completo del contacto"
                      full
                      error={getFormikArrayError(formik, "referencias", index, "contact_name")}
                    >
                      <TextField
                        size="lendia_input"
                        name={`referencias[${index}].contact_name`}
                        fullWidth
                        autoComplete="off"
                        placeholder="Nombre del contacto"
                        value={referencia.contact_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={bloqueado}
                        inputProps={{ "data-testid": `expediente-referencias-clientes-${index}-contacto-input` }}
                      />
                    </FieldWrapper>
                  )}

                  {form.isVisible("email") && (
                    <FieldWrapper
                      label="Correo electrónico"
                      error={getFormikArrayError(formik, "referencias", index, "email")}
                    >
                      <TextField
                        size="lendia_input"
                        name={`referencias[${index}].email`}
                        fullWidth
                        autoComplete="off"
                        placeholder="contacto@empresa.com"
                        value={referencia.email}
                        onChange={(event) =>
                          setFieldValue(`referencias[${index}].email`, (event.target.value || "").toLowerCase())
                        }
                        onBlur={formik.handleBlur}
                        disabled={bloqueado}
                        inputProps={{
                          maxLength: 100,
                          "data-testid": `expediente-referencias-clientes-${index}-email-input`,
                        }}
                      />
                    </FieldWrapper>
                  )}

                  {form.isVisible("phone_number") && (
                    <FieldWrapper
                      label="Teléfono de contacto"
                      error={
                        getFormikArrayError(formik, "referencias", index, "country_code") ||
                        getFormikArrayError(formik, "referencias", index, "phone")
                      }
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <CountrySelect
                          value={referencia.country_code}
                          onChange={(valor) => setFieldValue(`referencias[${index}].country_code`, valor)}
                          disabled={bloqueado}
                        />
                        <TextField
                          autoComplete="off"
                          size="lendia_input"
                          name={`referencias[${index}].phone`}
                          fullWidth
                          placeholder="55 1234 5678"
                          value={referencia.phone}
                          onChange={(event) =>
                            setFieldValue(`referencias[${index}].phone`, event.target.value.replace(/[^0-9]/g, ""))
                          }
                          onBlur={formik.handleBlur}
                          disabled={bloqueado}
                          inputProps={{
                            maxLength: 10,
                            inputMode: "numeric",
                            "data-testid": `expediente-referencias-clientes-${index}-telefono-input`,
                          }}
                        />
                        <TextField
                          autoComplete="off"
                          size="lendia_input"
                          name={`referencias[${index}].phone_extension`}
                          placeholder="Ext."
                          value={referencia.phone_extension}
                          onChange={(event) =>
                            setFieldValue(
                              `referencias[${index}].phone_extension`,
                              event.target.value.replace(/[^0-9]/g, ""),
                            )
                          }
                          onBlur={formik.handleBlur}
                          disabled={bloqueado}
                          sx={{ maxWidth: 96 }}
                          inputProps={{
                            maxLength: 5,
                            inputMode: "numeric",
                            "data-testid": `expediente-referencias-clientes-${index}-extension-input`,
                          }}
                        />
                      </Box>
                    </FieldWrapper>
                  )}
                </Box>
              </AccordionCard>
            ))}
          </Box>

          <StepActions
            onBack={volverAlExpediente}
            onNext={formik.submitForm}
            backLabel="Regresar al Expediente"
            nextLabel="Continuar a proveedores"
            nextDisabled={bloqueado || !todasCompletas}
            backTestId="expediente-referencias-clientes-back-button"
            nextTestId="expediente-referencias-clientes-continue-button"
          />
        </Fragment>
      )}
    </Box>
  );
};
